import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Lock, CheckCircle2, ArrowLeft } from "lucide-react";
import Emblem from "@/components/Emblem";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const input = "w-full rsp-card pl-10 pr-3 py-3 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] text-sm";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const setup = params.get("setup") === "1";
  const copy = setup
    ? { eyebrow: "Account Setup", heading: "Set Your Password", intro: "Create a password to finish setting up your RSP Riverside account.", submit: "Set Password", done: "Your password is set.", toast: "Password set. You can now sign in." }
    : { eyebrow: "Password Reset", heading: "Set New Password", intro: "Choose a new password for your account.", submit: "Reset Password", done: "Your password has been reset.", toast: "Password reset. You can now sign in." };
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (form.password !== form.confirm) return toast.error("Passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.password });
      setDone(true);
      toast.success(copy.toast);
      setTimeout(() => navigate("/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(apiError(err, "Could not reset password."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex justify-center bg-[var(--rsp-bg)]">
      <div className="w-full max-w-[440px] px-6 flex flex-col items-center text-center pt-16 pb-12">
        <Emblem size={120} />
        <div className="eyebrow mt-4" style={{ letterSpacing: "0.28em" }}>{copy.eyebrow}</div>
        <h1 className="heading gold" style={{ fontSize: "2.4rem" }}>{copy.heading}</h1>

        {!token ? (
          <div className="w-full mt-6 rsp-card p-6" data-testid="reset-no-token">
            <p className="text-[var(--rsp-cream)]">This reset link is missing or invalid.</p>
            <Link to="/forgot-password" className="gold hover:brightness-110 text-sm mt-2 inline-block">Request a new link</Link>
          </div>
        ) : done ? (
          <div className="w-full mt-6 rsp-card p-6 space-y-2" data-testid="reset-done">
            <CheckCircle2 size={40} className="gold mx-auto" />
            <p className="text-[var(--rsp-cream)]">{copy.done}</p>
            <p className="text-[var(--rsp-gray)] text-sm">Redirecting you to sign in…</p>
          </div>
        ) : (
          <>
            <p className="text-[var(--rsp-gray)] text-sm mt-1 mb-6">{copy.intro}</p>
            <form onSubmit={submit} className="w-full space-y-3" data-testid="reset-form">
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" />
                <input className={input} type="password" placeholder="New password (min 8 characters)" value={form.password} onChange={(e) => set("password", e.target.value)} required data-testid="reset-password" />
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--rsp-muted)]" />
                <input className={input} type="password" placeholder="Confirm new password" value={form.confirm} onChange={(e) => set("confirm", e.target.value)} required data-testid="reset-confirm" />
              </div>
              <button type="submit" disabled={busy} className="btn-gold w-full py-3 disabled:opacity-60" data-testid="reset-submit">
                {busy ? "Saving…" : copy.submit}
              </button>
            </form>
            <Link to="/login" className="text-sm text-[var(--rsp-gray)] hover:text-[var(--rsp-cream)] mt-5 inline-flex items-center gap-1">
              <ArrowLeft size={14} /> Back to Sign In
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
